import { useEffect, useState } from 'react';

import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import DateRangeIcon from '@mui/icons-material/DateRange';
import { ClickAwayListener, Popper, Typography } from '@mui/material';

import dayjs from 'dayjs';
import { useSnackbar } from 'notistack';

import CalendarDateRangePicker from '@/shared/ui/CalendarDateRangePicker';

import {
  DateRangeContainer,
  FilterFieldContainer,
  FilterLabel,
  arrowForwardIconSx,
  dateRangeIconSx,
} from '../GeneratikTableFilterPanel.styles';
import { DateRangeFieldProps } from '../GeneratikTableFilterPanel.types';

const dateRangeTriggerTextSx = {
  flex: 1,
  fontSize: '0.75rem',
  fontWeight: 400,
  lineHeight: 1.6,
  color: (theme) => theme.palette.grey[600],
  textAlign: 'center' as const,
};

const DateRangeFilterField = ({
  field,
  value,
  onChange,
  t,
  disabled,
  autoOpen,
  disablePast,
  shouldDisableDate,
  hideClear,
}: DateRangeFieldProps) => {
  const { enqueueSnackbar } = useSnackbar();
  const [anchorEl, setAnchorEl] = useState<HTMLDivElement | null>(null);
  const [isOpen, setIsOpen] = useState(false);

  useEffect(() => {
    if (!autoOpen) return;
    const timer = setTimeout(() => setIsOpen(true), 300);
    return () => clearTimeout(timer);
  }, [autoOpen]);

  return (
    <FilterFieldContainer data-testid={`filter-field-${field.id}`}>
      <FilterLabel>{field.label}</FilterLabel>
      <DateRangeContainer
        ref={setAnchorEl}
        onClick={() => !disabled && setIsOpen(true)}
        sx={{
          cursor: disabled ? 'default' : 'pointer',
          opacity: disabled ? 0.5 : 1,
          pointerEvents: disabled ? 'none' : 'auto',
        }}
      >
        <DateRangeIcon sx={dateRangeIconSx} />
        <Typography sx={dateRangeTriggerTextSx}>
          {value.start
            ? dayjs(value.start).format('DD.MM.YYYY')
            : field.startPlaceholder || t('starting_date')}
        </Typography>
        <ArrowForwardIcon sx={arrowForwardIconSx} />
        <Typography sx={dateRangeTriggerTextSx}>
          {value.end
            ? dayjs(value.end).format('DD.MM.YYYY')
            : field.endPlaceholder || t('ending_date')}
        </Typography>
      </DateRangeContainer>
      <ClickAwayListener
        onClickAway={(event) => {
          if (anchorEl && event.target instanceof Node && anchorEl.contains(event.target)) return;
          setIsOpen(false);
        }}
      >
        <Popper
          open={!!anchorEl && isOpen}
          anchorEl={anchorEl}
          placement="bottom-start"
          sx={{ zIndex: (theme) => theme.zIndex.modal }}
        >
          <CalendarDateRangePicker
            data-testid={`filter-field-${field.id}-calendar`}
            startDate={value.start ? dayjs(value.start) : null}
            endDate={value.end ? dayjs(value.end) : null}
            isDateUnavailable={shouldDisableDate}
            disablePast={disablePast}
            minDate={disablePast ? dayjs().startOf('day') : undefined}
            showDateInputs={false}
            hideClear={hideClear}
            restartOnCompleteRangeClick
            onChange={(newStart, newEnd) => {
              if (!newStart || !newEnd) {
                onChange?.({ start: newStart?.format('YYYY-MM-DD'), end: undefined });
                return;
              }
              const today = dayjs().startOf('day');
              if (
                disablePast &&
                (newStart.isBefore(today, 'day') || newEnd.isBefore(today, 'day'))
              ) {
                enqueueSnackbar(t('Past dates are not permitted') as string, {
                  variant: 'warning',
                });
                return;
              }
              onChange?.({
                start: newStart.format('YYYY-MM-DD'),
                end: newEnd.format('YYYY-MM-DD'),
              });
              setIsOpen(false);
            }}
          />
        </Popper>
      </ClickAwayListener>
    </FilterFieldContainer>
  );
};

export default DateRangeFilterField;
